export interface StudentDetails {
  id: string
  name: string
  studentId: string
  class: string
  house: string
  careerPath: string
  gpa: string
  status: "active" | "inactive" | "suspended"
  profileImage?: string
}

export interface Subject {
  name: string
  grade: string
  status: "passing" | "at-risk"
  icon: string
  bgColor: string
  textColor: string
}

export interface Guardian {
  name: string
  relationship: string
  phone: string
  email: string
  address: string
}

export interface Achievement {
  title: string
  description: string
  icon: string
  bgColor: string
  textColor: string
}

export interface Skill {
  name: string
  level: string
  progress: number
}

export interface FinancialStatus {
  session: string
  outstanding: number
  paid: number
  total: number
  status: "paid" | "partially-paid" | "unpaid"
}

export interface Task {
  id: string
  title: string
  subject: string
  dueDate: string
  status: "completed" | "pending" | "overdue"
  priority: "high" | "medium" | "low"
}

export interface ActivityLog {
  id: string
  action: string
  timestamp: string
  category: "academic" | "attendance" | "behavior" | "extracurricular"
}

export interface CareerPath {
  name: string
  icon: string
  // progress: number
  status: "primary" | "exploring" | "interested"
  bgColor: string
  textColor: string
}

export const sampleStudent: StudentDetails = {
  id: "ESCP-2023-892",
  name: "Chinedu Okafor",
  studentId: "ESCP-2023-892",
  class: "JSS 2",
  house: "Gold Arm",
  careerPath: "Aspiring Robotics Engineer",
  gpa: "3.8",
  status: "active",
}

export const sampleSubjects: Subject[] = [
  {
    name: "Mathematics",
    grade: "A",
    status: "passing",
    icon: "calculator",
    bgColor: "bg-blue-100",
    textColor: "text-blue-600",
  },
  {
    name: "Basic Science",
    grade: "B+",
    status: "passing",
    icon: "flask-conical",
    bgColor: "bg-purple-100",
    textColor: "text-purple-600",
  },
  {
    name: "Intro to Coding",
    grade: "A",
    status: "passing",
    icon: "code",
    bgColor: "bg-orange-100",
    textColor: "text-orange-600",
  },
  {
    name: "French",
    grade: "D",
    status: "at-risk",
    icon: "globe",
    bgColor: "bg-red-100",
    textColor: "text-red-600",
  },
]

export const sampleGuardian: Guardian = {
  name: "Mrs. Ngozi Okafor",
  relationship: "Mother",
  phone: "+234 803 555 0123",
  email: "ngozi.okafor@email.com",
  address: "15, Adeola Odeku St, VI, Lagos",
}

export const sampleAchievements: Achievement[] = [
  {
    title: "Science Fair Winner 2023",
    description: "Built a working solar fan model.",
    icon: "trophy",
    bgColor: "bg-yellow-100",
    textColor: "text-yellow-600",
  },
  {
    title: "Punctuality Prefect",
    description: "Awarded for 100% attendance.",
    icon: "medal",
    bgColor: "bg-blue-100",
    textColor: "text-blue-600",
  },
]

export const sampleSkills: Skill[] = [
  { name: "Scratch Programming", level: "Advanced", progress: 90 },
  { name: "Problem Solving", level: "Intermediate", progress: 75 },
  { name: "Teamwork", level: "Advanced", progress: 85 },
]

export const sampleFinancial: FinancialStatus = {
  session: "2023/2024",
  outstanding: 45000,
  paid: 105000,
  total: 150000,
  status: "partially-paid",
}

export const sampleTasks: Task[] = [
  {
    id: "1",
    title: "Mathematics Assignment",
    subject: "Mathematics",
    dueDate: "2024-01-15",
    status: "completed",
    priority: "high",
  },
  {
    id: "2",
    title: "Science Project",
    subject: "Basic Science",
    dueDate: "2024-01-20",
    status: "pending",
    priority: "high",
  },
  {
    id: "3",
    title: "French Oral Presentation",
    subject: "French",
    dueDate: "2024-01-18",
    status: "overdue",
    priority: "medium",
  },
  {
    id: "4",
    title: "Coding Challenge",
    subject: "Intro to Coding",
    dueDate: "2024-01-22",
    status: "pending",
    priority: "low",
  },
]

export const sampleActivityLogs: ActivityLog[] = [
  {
    id: "1",
    action: "Completed Mathematics Assignment",
    timestamp: "2 hours ago",
    category: "academic",
  },
  {
    id: "2",
    action: "Attended Science Club Meeting",
    timestamp: "1 day ago",
    category: "extracurricular",
  },
  {
    id: "3",
    action: "Perfect Attendance This Week",
    timestamp: "3 days ago",
    category: "attendance",
  },
  {
    id: "4",
    action: "Received Commendation for Behavior",
    timestamp: "5 days ago",
    category: "behavior",
  },
]

export const sampleCareerPaths: CareerPath[] = [
  {
    name: "Robotics Engineer",
    icon: "code",
    status: "primary",
    bgColor: "bg-teal-100",
    textColor: "text-teal-600",
  },
  {
    name: "Data Scientist",
    icon: "trending-up",
    status: "exploring",
    bgColor: "bg-blue-100",
    textColor: "text-blue-600",
  },
  {
    name: "Medical Doctor",
    icon: "stethoscope",  
    status: "interested",
    bgColor: "bg-purple-100",
    textColor: "text-purple-600",
  },
]

export const hobbies = ["Chess", "Football", "Comic Books", "Lego Building"]

export const nigerianSubjects: Array<{
  name: string
  ca: number
  exam: number
  total: number
  grade: string
  color: string
}> = [
  { name: "Mathematics", ca: 35, exam: 53, total: 88, grade: "A", color: "teal" },
  { name: "English Language", ca: 30, exam: 45, total: 75, grade: "B", color: "blue" },
  { name: "Basic Science", ca: 38, exam: 54, total: 92, grade: "A+", color: "orange" },
  { name: "Civic Education", ca: 32, exam: 48, total: 80, grade: "A", color: "purple" },
  { name: "Business Studies", ca: 25, exam: 40, total: 65, grade: "C", color: "yellow" },
  { name: "French", ca: 28, exam: 42, total: 70, grade: "B", color: "gray" },
  { name: "Home Economics", ca: 36, exam: 44, total: 80, grade: "A", color: "pink" },
  { name: "Intro to Technology", ca: 22, exam: 46, total: 68, grade: "B", color: "indigo" },
]

export const enrolledSubjects = ["Mathematics", "Basic Science", "Intro to Coding", "French"]

export const careerPathsData = [
  { name: "Robotics", focus: "Engineering", color: "blue", icon: "manufacturing" as const },
  { name: "Software Dev", focus: "Coding", color: "orange", icon: "code" as const },
  { name: "Sciences", focus: "Core Curriculum", color: "teal", icon: "science" as const },
]

export const achievementsData = [
  {
    title: "Science Fair 1st Place",
    description: "National Competition '23",
    icon: "trophy" as const,
    color: "yellow",
  },
  { title: "Math Olympiad Bronze", description: "State Level Achievement", icon: "award" as const, color: "blue" },
  { title: "Perfect Attendance", description: "Entire 2nd Term", icon: "star" as const, color: "teal" },
]
